// src/components/ui/Button.jsx
import React from "react";
import Icon from "./Icon";

/**
 * Button.
 *
 * Per your feedback: white text on green looked washed out. The "gold"
 * variant (which renders the brand-green CTAs throughout the app) now
 * uses nile-blue (#23364C) text instead. Same color in both light and
 * dark themes — green is the same green in both themes.
 *
 * The "panel" variant (used on dark panels with a white button) also
 * gets explicit dark text for the same contrast reasons.
 */
export default function Button({
  children,
  type = "button",
  variant = "gold",
  size = "md",
  full = false,
  loading = false,
  disabled = false,
  icon = null,
  iconRight = false,
  className = "",
  onClick,
  ...rest
}) {
  const base =
    "inline-flex items-center justify-center gap-2 font-medium " +
    "rounded-full transition-all duration-200 select-none " +
    "disabled:opacity-50 disabled:cursor-not-allowed " +
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold-border)]";

  const sizes = {
    sm: "px-4 py-2 text-[13px]",
    md: "px-5 py-[10px] text-[14px]",
    lg: "px-7 py-[14px] text-[15px]",
  }[size];

  const variants = {
    // Green primary CTA — nile-blue text per "white gets washed out" feedback
    gold:
      "text-[#23364C] bg-[#2EE56B] hover:bg-[#2BD962] " +
      "hover:-translate-y-[1px] active:translate-y-0 " +
      "shadow-[0_4px_14px_rgba(46,229,107,0.30)]",
    outline:
      "bg-transparent text-[var(--txt)] border border-[var(--border-2)] " +
      "hover:border-[var(--gold)] hover:text-[var(--gold)]",
    ghost:
      "bg-[var(--surf-2)] text-[var(--txt)] border border-[var(--border)] " +
      "hover:bg-[var(--surf-3)]",
    danger:
      "bg-[var(--red-dim)] text-[var(--red)] border border-[var(--red-border)] " +
      "hover:bg-[rgba(214,69,60,0.12)]",
    // Dark text on white pill — used on the dark panel CTAs
    panel:
      "text-[#23364C] bg-white hover:bg-[#F6F9FC] " +
      "hover:-translate-y-[1px] active:translate-y-0",
  }[variant];

  const width = full ? "w-full" : "";

  const renderIcon = () => {
    if (loading) return <Icon name="loader-2" size={16} />;
    if (!icon) return null;
    return typeof icon === "string" ? <Icon name={icon} size={16} /> : icon;
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`${base} ${sizes} ${variants} ${width} ${className}`}
      {...rest}
    >
      {!iconRight && renderIcon()}
      {children}
      {iconRight && renderIcon()}
    </button>
  );
}
